import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type {
    PublicationHistoryCreation,
    PublicationHistoryItem,
    PublicationHistoryMutation,
} from '~/shared/data-contract/publication-history';
import {
    publicationHistoryItemArraySchema,
    publicationHistoryItemSchema,
} from '~/shared/data-contract/publication-history';
import { getUserHardSoftSkillsQueryKey } from '~/shared/query-hooks/hard-soft-skills';

import { nullifyEmptyStringValues } from '../../common/form';

export const usePublicationHistoryQuery = (
    userId: string,
    enabled: boolean,
): UseQueryResult<readonly PublicationHistoryItem[]> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const publicationHistoryUrl = `/users/${userId}/publication-history` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(publicationHistoryItemArraySchema, {
                pathname: publicationHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [publicationHistoryUrl, 'GET', language.code],
    });
};

export const useOnePublicationHistoryQuery = (
    userId: string,
    publicationHistoryId: string,
    enabled: boolean,
): UseQueryResult<PublicationHistoryItem> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const onePublicationHistoryUrl = `/users/${userId}/publication-history/${publicationHistoryId}` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(publicationHistoryItemSchema, {
                pathname: onePublicationHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [onePublicationHistoryUrl, 'GET', language.code],
    });
};

export type PublicationHistoryMutations = {
    readonly createPublicationMutation: UseMutationResult<
        PublicationHistoryItem,
        unknown,
        Omit<PublicationHistoryItem, 'id'>
    >;
    readonly deletePublicationMutation: UseMutationResult<void, unknown, string>;
    readonly patchPublicationMutation: UseMutationResult<PublicationHistoryItem, unknown, PublicationHistoryItem>;
};

export const usePublicationHistoryMutations = (userId: string): PublicationHistoryMutations => {
    const queryClient = useQueryClient();
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const invalidatePublicationCache = async (publicationHistoryId: Maybe<string>): Promise<void> => {
        if (publicationHistoryId.isJust) {
            await queryClient.invalidateQueries({
                queryKey: [`/users/${userId}/publication-history/${publicationHistoryId}`, 'GET'],
            });
        }
        await queryClient.invalidateQueries({ queryKey: [`/users/${userId}/publication-history`, 'GET'] });
        await queryClient.invalidateQueries({ queryKey: getUserHardSoftSkillsQueryKey(userId) });
    };

    const createPublicationMutation = useMutation({
        mutationFn: async (values: Omit<PublicationHistoryItem, 'id'>): Promise<PublicationHistoryItem> => {
            const { publicationName, skills, ...restValues } = values;
            const data: PublicationHistoryCreation = {
                ...cleanDeep(restValues),
                publicationName,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.postJson(publicationHistoryItemSchema, {
                pathname: `/users/${userId}/publication-history`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (values: PublicationHistoryItem) => invalidatePublicationCache(Maybe.just(values.id)),
    });

    const patchPublicationMutation = useMutation({
        mutationFn: async (values: PublicationHistoryItem): Promise<PublicationHistoryItem> => {
            const { id, publicationName, skills, ...restValues } = values;
            const data: PublicationHistoryMutation = {
                ...cleanDeep(nullifyEmptyStringValues(restValues), { nullValues: false }),
                id,
                publicationName,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.patchJson(publicationHistoryItemSchema, {
                pathname: `/users/${userId}/publication-history/${values.id}`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, values) => invalidatePublicationCache(Maybe.just(values.id)),
    });

    const deletePublicationMutation: UseMutationResult<void, unknown, string> = useMutation({
        mutationFn: async (id: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `/users/${userId}/publication-history/${id}`,
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: () => invalidatePublicationCache(Maybe.nothing()),
    });

    return {
        createPublicationMutation,
        deletePublicationMutation,
        patchPublicationMutation,
    };
};
